import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";

import PublicLayout from "./components/PublicLayout";
import SplashScreen from "./components/SplashScreen";
import ScrollToTop from "./components/ScrollToTop";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import TeamPage from "./pages/TeamPage";
import LawyerPage from "./pages/LawyerPage";
import ContactPage from "./pages/ContactPage";

import AdminLogin from "./pages/admin/AdminLogin";
import AuthCallback from "./pages/admin/AuthCallback";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminHome from "./pages/admin/AdminHome";
import AdminAbout from "./pages/admin/AdminAbout";
import AdminServices from "./pages/admin/AdminServices";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminContact from "./pages/admin/AdminContact";
import AdminMessages from "./pages/admin/AdminMessages";

// First-load splash: force Turkish language on a fresh session.
// Skip splash on admin routes.
const isAdminPath =
  typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
const splashAlreadyShown =
  typeof window !== "undefined" && sessionStorage.getItem("ap_splash_shown") === "1";
if (typeof window !== "undefined" && !splashAlreadyShown && !isAdminPath) {
  localStorage.setItem("ap_lang", "tr");
}

function AppRouter() {
  const location = useLocation();
  // Process Emergent OAuth callback BEFORE any other route logic to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/hakkimizda" element={<AboutPage />} />
        <Route path="/hizmetler" element={<ServicesPage />} />
        <Route path="/ekip" element={<TeamPage />} />
        <Route path="/ekip/:id" element={<LawyerPage />} />
        <Route path="/iletisim" element={<ContactPage />} />
      </Route>

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="home" element={<AdminHome />} />
        <Route path="about" element={<AdminAbout />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="contact" element={<AdminContact />} />
        <Route path="messages" element={<AdminMessages />} />
      </Route>
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(
    () => !splashAlreadyShown && !isAdminPath
  );

  useEffect(() => {
    if (!showSplash) return;
    const t = setTimeout(() => {
      setShowSplash(false);
      sessionStorage.setItem("ap_splash_shown", "1");
    }, 3000);
    return () => clearTimeout(t);
  }, [showSplash]);

  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            {showSplash && <SplashScreen />}
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
